import { DOC_IMAGE_WIDTH, type DocImageSize, docImageUrl, documentImageIds, inlineDocumentImages } from '@bqcrm/shared';
import { mergeAttributes, Node } from '@tiptap/react';
import { useQueries, useQuery } from '@tanstack/react-query';

/** Węzeł edytora: grafika dokumentu (<img data-grafika data-rozmiar>) — w wierszu, wyrównanie jak akapitu. */
export const GrafikaNode = Node.create({
  name: 'grafika',
  inline: true,
  group: 'inline',
  atom: true,
  draggable: true,
  selectable: true,
  addAttributes() {
    return {
      fileId: { default: null, parseHTML: (el) => el.getAttribute('data-grafika'), renderHTML: (a) => ({ 'data-grafika': a.fileId as string }) },
      rozmiar: { default: 'm', parseHTML: (el) => el.getAttribute('data-rozmiar') ?? 'm', renderHTML: (a) => ({ 'data-rozmiar': a.rozmiar as string }) },
    };
  },
  parseHTML() {
    return [{ tag: 'img[data-grafika]' }];
  },
  renderHTML({ node, HTMLAttributes }) {
    const size = (node.attrs.rozmiar as DocImageSize) in DOC_IMAGE_WIDTH ? (node.attrs.rozmiar as DocImageSize) : 'm';
    return ['img', mergeAttributes(HTMLAttributes, { src: docImageUrl(node.attrs.fileId as string), style: `width: ${DOC_IMAGE_WIDTH[size]}`, alt: '' })];
  },
});

/** Plik z API jako data:… (podgląd dokumentu w izolowanej ramce nie pobiera adresów aplikacji). */
async function fetchDataUri(url: string): Promise<string | null> {
  const res = await fetch(url, { credentials: 'same-origin' });
  if (!res.ok) return null;
  const blob = await res.blob();
  return new Promise((resolve) => {
    const r = new FileReader();
    r.onload = () => resolve(typeof r.result === 'string' ? r.result : null);
    r.onerror = () => resolve(null);
    r.readAsDataURL(blob);
  });
}

/** Treść szablonu z grafikami jako obrazy (ta sama funkcja co przy tworzeniu dokumentu na serwerze). */
export function useInlinedImages(html: string): string {
  const ids = documentImageIds(html);
  const results = useQueries({
    queries: ids.map((id) => ({ queryKey: ['doc-image', id], queryFn: () => fetchDataUri(docImageUrl(id)), staleTime: Infinity })),
  });
  const map = new Map(ids.map((id, i) => [id, results[i]?.data ?? null]));
  return inlineDocumentImages(html, (id) => map.get(id));
}

/** Logo firmy (data:…) do podglądu; null — firma bez logo. */
export function useCompanyLogo(companyId: string | null | undefined, logoFileId: string | null | undefined): string | null {
  const q = useQuery({
    queryKey: ['company-logo', companyId, logoFileId],
    queryFn: () => fetchDataUri(`/api/companies/${companyId}/logo`),
    enabled: !!companyId && !!logoFileId,
    staleTime: Infinity,
  });
  return logoFileId ? (q.data ?? null) : null;
}
